import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { useContentApi } from '@/composables/useCmsContentApi'
import type { Topic } from '@/types/Topic'
import type { Item } from '@/types/Ibexa'
import { IbexaContentTypeIdentifier } from '@/enums/IbexaContentTypeIdentifier'
import {
  getItemContentType,
  depthFilter,
  mapSubjectContent,
  mapSubjectResource 
} from '@/utils'

import { useAppRouting } from './useAppRouting'
import RouteParam from '@/enums/RouteParam'

export const useTopic = () => {
  const { getCurrentTopic, getTopicGuideChild, getTopicSubtree } = useContentApi()
  const { getContentIdFromRouteParam } = useAppRouting()

  const topicId = getContentIdFromRouteParam(RouteParam.Topic)

  const { data: currentTopic, isSuccess: currentTopicStatus, isLoading: LoadingTopics  } = useQuery<Topic>({
    queryKey: ['currentTopic', topicId],
    queryFn: () =>  getCurrentTopic(topicId),
    staleTime: Infinity
  })

  const { data: subtree, isLoading: LoadingSubtree } = useQuery<Item[]>({
    queryKey: ['subtree', topicId],
    queryFn: () => getTopicSubtree(currentTopic.value?.pathString as string),
    staleTime: Infinity,
    enabled: currentTopicStatus
  })

  const { data: topicGuide } = useQuery({
    queryKey: ['topicGuide', topicId],
    queryFn: () => getTopicGuideChild(topicId),
    staleTime: Infinity,
    enabled: !!topicId
  })

  const subjectContents = computed(() => subtree.value
    ?.filter((item) => getItemContentType(item) === IbexaContentTypeIdentifier.vgsSubjectContent)
    ?.map(mapSubjectContent) || [])

  const subjectResources = computed(() => (subtree.value || [])
    ?.filter((item) => getItemContentType(item) === IbexaContentTypeIdentifier.vgsSubjectResource)
    ?.map(mapSubjectResource)
    ?.filter((subjectResource) =>  depthFilter(currentTopic?.value, subjectResource, 2))
  )

  const isLoading = computed(() => {
    return LoadingTopics.value || LoadingSubtree.value
  })

  return {
    currentTopic,
    topicGuide,
    subjectContents,
    subjectResources,
    isLoading,
  }
}